import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Save, Image, X, Calendar } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TournamentInfoPage() {
  const { token, organization } = useAuth();
  const { t } = useTranslation();
  const [info, setInfo] = useState({
    tournament_description: '',
    event_date: '',
    event_location: '',
    rules: '',
    contact_info: '',
    additional_notes: '',
    images: [],
    host_team_image: '',
    host_team_description: ''
  });
  const [saving, setSaving] = useState(false);
  const imageInputRef = useRef(null);
  const [upcomingMatches, setUpcomingMatches] = useState([]);
  const [teams, setTeams] = useState([]);

  useEffect(() => {
    if (organization) {
      fetch(`${API_URL}/api/tournament-info/${organization.org_id}`)
        .then(r => r.json())
        .then(data => {
          setInfo({
            tournament_description: data.tournament_description || '',
            event_date: data.event_date || '',
            event_location: data.event_location || '',
            rules: data.rules || '',
            contact_info: data.contact_info || '',
            additional_notes: data.additional_notes || '',
            images: data.images || [],
            host_team_image: data.host_team_image || '',
            host_team_description: data.host_team_description || ''
          });
        })
        .catch(() => {});
      // Fetch matches and teams for schedule display
      fetch(`${API_URL}/api/matches/${organization.org_id}`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json()).then(m => setUpcomingMatches(m.filter(x => x.status === 'scheduled'))).catch(() => {});
      fetch(`${API_URL}/api/teams/${organization.org_id}`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json()).then(setTeams).catch(() => {});
    }
  }, [organization]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2MB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      setInfo(prev => ({ ...prev, images: [...(prev.images || []), ev.target.result] }));
    };
    reader.readAsDataURL(file);
    imageInputRef.current.value = '';
  };

  const removeImage = (index) => {
    setInfo(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/tournament-info/${organization.org_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(info)
      });
      if (res.ok) toast.success('Tournament info saved!');
      else toast.error('Failed to save');
    } catch { toast.error('Error saving'); }
    finally { setSaving(false); }
  };

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="tournament-info-page">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            Tournament Info
          </h1>
          <p className="text-gray-600 mt-2">Add information about your tournament and event</p>
        </div>

        <Card className="bg-white border-gray-200">
          <CardContent className="p-6 space-y-6">
            <div className="space-y-2">
              <Label className="text-gray-700">Tournament Description</Label>
              <Textarea
                value={info.tournament_description}
                onChange={(e) => setInfo({...info, tournament_description: e.target.value})}
                placeholder="Describe your tournament, its history, and purpose..."
                className="bg-gray-100 border-gray-200 text-black min-h-[100px]"
                data-testid="info-description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label className="text-gray-700">Event Date</Label>
                <Input value={info.event_date} onChange={(e) => setInfo({...info, event_date: e.target.value})} placeholder="e.g., July 15-20, 2026" className="bg-gray-100 border-gray-200 text-black" data-testid="info-date" />
              </div>
              <div className="space-y-2">
                <Label className="text-gray-700">Event Location</Label>
                <Input value={info.event_location} onChange={(e) => setInfo({...info, event_location: e.target.value})} placeholder="e.g., Toronto Sports Complex" className="bg-gray-100 border-gray-200 text-black" data-testid="info-location" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Rules & Format</Label>
              <Textarea value={info.rules} onChange={(e) => setInfo({...info, rules: e.target.value})} placeholder="Tournament rules, format details, eligibility requirements..." className="bg-gray-100 border-gray-200 text-black min-h-[100px]" data-testid="info-rules" />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Contact Information</Label>
              <Input value={info.contact_info} onChange={(e) => setInfo({...info, contact_info: e.target.value})} placeholder="Email, phone, or social media for inquiries" className="bg-gray-100 border-gray-200 text-black" data-testid="info-contact" />
            </div>

            <div className="space-y-2">
              <Label className="text-gray-700">Additional Notes</Label>
              <Textarea value={info.additional_notes} onChange={(e) => setInfo({...info, additional_notes: e.target.value})} placeholder="Any other information participants or spectators should know..." className="bg-gray-100 border-gray-200 text-black min-h-[80px]" data-testid="info-notes" />
            </div>

            {/* Image Upload Section */}
            <div className="space-y-3">
              <Label className="text-gray-700 flex items-center gap-2">
                <Image size={16} className="text-emerald-500" />
                Tournament Images
              </Label>
              
              {info.images && info.images.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {info.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt={`Tournament ${i+1}`} className="w-full h-32 object-cover rounded-lg border border-gray-200" />
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X size={12} className="text-black" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              <input type="file" ref={imageInputRef} accept="image/*" onChange={handleImageUpload} className="hidden" />
              <Button
                type="button"
                variant="outline"
                onClick={() => imageInputRef.current?.click()}
                className="border-gray-200 text-gray-700 hover:text-black"
                data-testid="add-image-btn"
              >
                <Image size={16} className="mr-2" />
                Add Image
              </Button>
              <p className="text-xs text-gray-500">Max 2MB per image. JPG, PNG, or WebP</p>
            </div>

            {/* Host Team Section */}
            <div className="space-y-3 border-t border-gray-200 pt-6">
              <Label className="text-gray-700 text-lg font-medium">About the Host Team</Label>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="space-y-2 flex-1">
                  {info.host_team_image ? (
                    <div className="relative group">
                      <img src={info.host_team_image} alt="Host team" className="w-full h-40 object-cover rounded-lg border border-gray-200" />
                      <button type="button" onClick={() => setInfo({...info, host_team_image: ''})} className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <X size={12} className="text-black" />
                      </button>
                    </div>
                  ) : (
                    <Button type="button" variant="outline" onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = 'image/*';
                      input.onchange = (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        if (file.size > 2 * 1024 * 1024) { toast.error('Image must be under 2MB'); return; }
                        const reader = new FileReader();
                        reader.onload = (ev) => setInfo(prev => ({...prev, host_team_image: ev.target.result}));
                        reader.readAsDataURL(file);
                      };
                      input.click();
                    }} className="border-gray-200 text-gray-700 hover:text-black w-full h-32">
                      <Image size={24} className="mr-2" /> Upload Host Team Image
                    </Button>
                  )}
                </div>
                <div className="space-y-2 flex-1">
                  <Textarea
                    value={info.host_team_description}
                    onChange={(e) => setInfo({...info, host_team_description: e.target.value})}
                    placeholder="Tell us about the host team or organization..."
                    className="bg-gray-100 border-gray-200 text-black min-h-[140px]"
                    data-testid="host-team-desc"
                  />
                </div>
              </div>
            </div>

            <Button onClick={handleSave} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-black" data-testid="save-info-btn">
              <Save size={18} className="mr-2" />
              {saving ? 'Saving...' : 'Save Information'}
            </Button>
          </CardContent>
        </Card>

        {/* Upcoming Schedule with Field Assignments */}
        {upcomingMatches.length > 0 && (
          <Card className="bg-white border-gray-200">
            <CardContent className="p-6">
              <h3 className="text-black font-heading text-lg uppercase mb-4 flex items-center gap-2">
                <Calendar size={18} className="text-emerald-500" />
                Upcoming Games & Field Assignments
              </h3>
              <div className="space-y-2">
                {(() => {
                  const teamMap = teams.reduce((m, t) => { m[t.team_id] = t.name; return m; }, {});
                  const stageLabels = { pool_a: 'Pool A', pool_b: 'Pool B', pool_c: 'Pool C', pool_d: 'Pool D', quarterfinal: 'QF', semifinal: 'SF', final: 'Final' };
                  return upcomingMatches.sort((a, b) => {
                    const so = { pool_a: 0, pool_b: 1, pool_c: 2, pool_d: 3, quarterfinal: 5, semifinal: 6, final: 7 };
                    return (so[a.stage] || 4) - (so[b.stage] || 4) || a.match_date.localeCompare(b.match_date) || a.match_time.localeCompare(b.match_time);
                  }).map(m => (
                    <div key={m.match_id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2 border border-gray-100">
                      <div className="flex items-center gap-3">
                        <span className="text-xs text-gray-500 w-12">{stageLabels[m.stage] || m.stage}</span>
                        <span className="text-black text-sm">{teamMap[m.home_team_id] || 'TBD'} vs {teamMap[m.away_team_id] || 'TBD'}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="text-gray-600 font-mono">{m.match_date} {m.match_time} MT</span>
                        {m.field ? (
                          <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded font-medium">{m.field}</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded">No field</span>
                        )}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
